import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { toSalary } from '../../util/SalaryDisplay';
import { League } from '../../../sdk/model';
import {
  Box,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardHeader,
  CardContent,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import FranchiseTaggingView from './FranchiseTaggingView';
import RookieOptionTaggingView from './RookieOptionTaggingView';

function ContractTaggingView() {
  const {leagueId, teamId} = useParams();
  const navigate = useNavigate();
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [showingDetails, setShowingDetails] = React.useState(false);
  const [view, setView] = React.useState('franchise');
  const user = useAppSelector((state) => state.user);
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );
  
  const playerMap = new Map(playerDataLoad.flatMap(team => team.players).map((player) => [player.id, player]));
  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicDraftData: false,
                                                            loadFullDraftData: false, loadContractData: true,
                                                            loadBasicAuctionData: false, loadFullAuctionData: false,
                                                            loadRosterData: true, 
                                                            isException: isException, setIsException: setIsException,
                                                            isLoadComplete: isLoadComplete, setLoadComplete: setLoadComplete});
  const team = league?.teams.find(team => teamId === team.id);
  const myTeam = team?.owners?.some(owner => owner.id === user.id);
  
  if (!isLoadComplete || !leagueId || !teamId) {
    return <Typography>Loading...</Typography>
  }
  return <Box>
    <Paper elevation={2}>
      <Box display="flex" justifyContent="center" width="100%">
        <Dialog open={showingDetails} maxWidth="lg" fullWidth>
          <DialogTitle>Tag Calculation Details</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Franchise Tag Eligibility:</Typography>
                <Typography fontWeight="bold">
                  Any player on an expired contract (final season in {league?.currentSeasonYear! - 1}) who has not already been franchised in three consecutive seasons.
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>1st and 2nd Franchise Tag Calculation:</Typography>
                <Typography fontWeight="bold">
                  The higher of (A) The average of Top 5 players' salaries at the same position in {league?.currentSeasonYear} or (B) 120% of previous season's salary
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>3rd Franchise Tag Calculation:</Typography>
                <Typography fontWeight="bold">
                  The higher of (A) the highest franchise tag value at any position in {league?.currentSeasonYear} or (B) 144% of previous season's salary
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>5th-Year Rookie Option Tag Eligibility:</Typography>
                <Typography fontWeight="bold">
                  Any player on a rookie contract taken in the first round of their draft going into their final contract year (final season in {league?.currentSeasonYear!})
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>5th-Year Rookie Option Tag Calculation:</Typography>
                <Typography fontWeight="bold">
                  The average of the Top 10 players' salaries at the same position in {league?.currentSeasonYear}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button color="primary" variant="contained" onClick={() => setShowingDetails(false)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ width: '100%' }}>
          <Card variant="outlined">
            <CardHeader 
              title="Minimum Tag Values at Position" 
              titleTypographyProps={{ align: 'center' }}
            />
            <CardContent>
              <Box justifyContent="center">
                <TableContainer component={Paper} sx={{ maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }} >
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Position</TableCell>
                        <TableCell align="right">Franchise Tag</TableCell>
                        <TableCell align="right">5th-Year Rookie Option</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(league?.currentSeason?.minimumTagValuesAtPosition!).map(([position, value]) => (
                        <TableRow
                          key={position}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">{position}</TableCell>
                          <TableCell align="right">{toSalary(value.franchiseTag)}</TableCell>
                          <TableCell align="right">{toSalary(value.rookieOption)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Button onClick={() => setShowingDetails(true)}>How are these values calculated?</Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Paper>
    
    <Divider sx={{ my: 3 }} />
    
    <Box display="flex" justifyContent="center">
      <Select
        value={teamId}
        onChange={(event) => navigate(`/league/${league?.id}/team/${event.target.value}/contractTags`)}
        size="small"
        sx={{ minWidth: 250 }}
      >
        {league?.teams.map((team) => (
          <MenuItem key={team.id} value={team.id}>
            {team.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <Paper elevation={2} sx={{ mt: 3 }}>
      <Tabs 
        value={view} 
        onChange={(event, value) => setView(value)}
        variant="fullWidth"
        sx={{
          '& .MuiTabs-indicator': {
            height: 4,
          },
        }}
      >
        <Tab label="Franchise Tag" value="franchise" />
        <Tab label="5th-Year Rookie Options" value="rookie" />
      </Tabs>
      {view === 'franchise' && <FranchiseTaggingView league={league!} team={team!} myTeam={myTeam!} playerMap={playerMap} />}
      {view === 'rookie' && <RookieOptionTaggingView league={league!} team={team!} myTeam={myTeam!} playerMap={playerMap} />}
    </Paper>
  </Box>
}

export default ContractTaggingView;
